<template>
  <Card>
    <template #title>
      Couleurs de l'interface
    </template>
    <template #content>
      <Message
        v-if="statusMessage"
        :severity="statusMessage.severity"
        class="mb-5"
      >
        {{ statusMessage.message }}
      </Message>

      <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <form
          class="space-y-5"
          @submit.prevent="saveColors"
        >
          <section class="space-y-3">
            <div>
              <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Suggestions
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Choisissez une combinaison harmonieuse, puis ajustez-la si besoin.
              </p>
            </div>

            <div class="grid max-h-80 gap-3 overflow-y-auto pr-1 sm:grid-cols-2">
              <button
                v-for="suggestion in colorSuggestions"
                :key="suggestion.name"
                type="button"
                class="rounded-lg border border-surface-200 bg-white p-3 text-left transition hover:border-[var(--ocv-secondary-500)] hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--ocv-secondary-500)] dark:border-surface-700 dark:bg-surface-900"
                :class="{
                  'border-[var(--ocv-secondary-500)] ring-1 ring-[var(--ocv-secondary-500)]': isActiveSuggestion(suggestion),
                }"
                @click="applySuggestion(suggestion)"
              >
                <span class="mb-3 flex h-8 overflow-hidden rounded-md border border-surface-200 dark:border-surface-700">
                  <span
                    class="h-full flex-1"
                    :style="{ backgroundColor: suggestion.primary }"
                  />
                  <span
                    class="h-full flex-1"
                    :style="{ backgroundColor: suggestion.secondary }"
                  />
                </span>
                <span class="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {{ suggestion.name }}
                </span>
                <span class="mt-1 block font-mono text-xs text-gray-500 dark:text-gray-400">
                  {{ suggestion.primary }} / {{ suggestion.secondary }}
                </span>
              </button>
            </div>
          </section>

          <div class="space-y-2">
            <label
              for="primary-color"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Couleur primary
            </label>
            <div class="flex gap-3">
              <input
                id="primary-color"
                v-model="form.primary"
                type="color"
                class="h-10 w-14 cursor-pointer rounded border border-surface-300 bg-white p-1"
              >
              <InputText
                v-model="form.primary"
                class="w-full font-mono"
                placeholder="#172033"
              />
            </div>
            <p
              v-if="errors.primary"
              class="text-sm text-red-600"
            >
              {{ errors.primary }}
            </p>
          </div>

          <div class="space-y-2">
            <label
              for="secondary-color"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Couleur secondary
            </label>
            <div class="flex gap-3">
              <input
                id="secondary-color"
                v-model="form.secondary"
                type="color"
                class="h-10 w-14 cursor-pointer rounded border border-surface-300 bg-white p-1"
              >
              <InputText
                v-model="form.secondary"
                class="w-full font-mono"
                placeholder="#10bb82"
              />
            </div>
            <p
              v-if="errors.secondary"
              class="text-sm text-red-600"
            >
              {{ errors.secondary }}
            </p>
          </div>

          <div class="space-y-2">
            <div>
              <label
                for="menu-hover-color"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Hover du menu de navigation
              </label>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Par défaut, il reste dérivé de la couleur secondary. Les presets réactivent toujours ce comportement.
              </p>
            </div>

            <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                :checked="hasCustomMenuHover"
                type="checkbox"
                class="h-4 w-4 rounded border-surface-300 text-[var(--ocv-secondary-600)] focus:ring-[var(--ocv-secondary-500)]"
                @change="toggleCustomMenuHover"
              >
              Utiliser une couleur personnalisée
            </label>

            <div
              v-if="hasCustomMenuHover"
              class="flex gap-3"
            >
              <input
                id="menu-hover-color"
                v-model="form.menuHover"
                type="color"
                class="h-10 w-14 cursor-pointer rounded border border-surface-300 bg-white p-1"
              >
              <InputText
                v-model="form.menuHover"
                class="w-full font-mono"
                placeholder="#1d3b35"
              />
            </div>
            <p
              v-if="errors.menuHover"
              class="text-sm text-red-600"
            >
              {{ errors.menuHover }}
            </p>
          </div>

          <div class="flex flex-wrap gap-3 pt-2">
            <Button
              type="submit"
              label="Enregistrer"
              icon="pi pi-check"
              :loading="isSaving"
              :disabled="hasErrors || isSaving"
            />
            <Button
              type="button"
              label="Annuler l'aperçu"
              severity="secondary"
              outlined
              :disabled="isSaving || !hasChanges"
              @click="cancelPreview"
            />
            <Button
              type="button"
              label="Réinitialiser"
              severity="secondary"
              text
              :disabled="isSaving"
              @click="resetToDefaults"
            />
          </div>
        </form>

        <div class="rounded-lg border border-surface-200 bg-surface-0 p-4 shadow-sm dark:border-surface-700 dark:bg-surface-800">
          <h3 class="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
            Aperçu
          </h3>
          <div class="overflow-hidden rounded-lg border border-surface-200 dark:border-surface-700">
            <div
              class="p-4 text-white"
              :style="{ backgroundColor: previewColors.primary }"
            >
              <div class="mb-4 text-sm font-semibold">
                Menu de navigation
              </div>
              <div class="space-y-2 text-sm">
                <div
                  class="rounded px-3 py-2"
                  :style="{ color: previewColors.secondary, backgroundColor: menuHoverBackground }"
                >
                  Tableau de bord
                </div>
                <div class="rounded px-3 py-2 text-white/80">
                  Marchés
                </div>
                <div class="rounded px-3 py-2 text-white/80">
                  Paramètres
                </div>
              </div>
            </div>
            <div class="space-y-3 bg-white p-4 dark:bg-surface-900">
              <button
                type="button"
                class="w-full rounded-md px-3 py-2 text-sm font-medium text-white"
                :style="{ backgroundColor: previewColors.primary }"
              >
                Action primary
              </button>
              <button
                type="button"
                class="w-full rounded-md px-3 py-2 text-sm font-medium"
                :style="{ backgroundColor: previewColors.secondary, color: previewSecondaryContrastColor }"
              >
                Action secondary
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import type { ResolvedThemeColors } from "~/utils/themeColorScale";

type StatusSeverity = "success" | "error" | "info";
type ColorSuggestion = ResolvedThemeColors & {
  name: string;
};

// Set when the form is opened in a dialog (top bar button); absent on the settings pages.
const dialogRef = inject<Ref<DynamicDialogInstance> | null>("dialogRef", null);

const {
  defaultThemeColors,
  getThemeColors,
  previewThemeColors,
  saveThemeColors,
} = useUserThemeColors();

const savedColors = ref<ResolvedThemeColors>({ ...getThemeColors() });
const form = reactive<ResolvedThemeColors>({ ...savedColors.value });
const isSaving = ref(false);
const statusMessage = ref<{ severity: StatusSeverity; message: string } | null>(null);
const colorSuggestions: ColorSuggestion[] = [
  {
    name: "OCV original",
    primary: "#468368",
    secondary: "#f0f0f0",
  },
  {
    name: "OCV Nuit",
    primary: "#172033",
    secondary: "#10bb82",
  },
  {
    name: "Marine & Menthe",
    primary: "#0f2742",
    secondary: "#2dd4bf",
  },
  {
    name: "Ardoise & Lime",
    primary: "#1f2937",
    secondary: "#84cc16",
  },
  {
    name: "Indigo & Cyan",
    primary: "#312e81",
    secondary: "#06b6d4",
  },
  {
    name: "Graphite & Ambre",
    primary: "#27272a",
    secondary: "#f59e0b",
  },
  {
    name: "Prune & Rose",
    primary: "#3b123f",
    secondary: "#f472b6",
  },
  {
    name: "Bleu nuit & Corail",
    primary: "#16213e",
    secondary: "#ff6b6b",
  },
  {
    name: "Sapin & Sauge",
    primary: "#14532d",
    secondary: "#a3e635",
  },
  {
    name: "Encre & Lavande",
    primary: "#1e1b4b",
    secondary: "#a78bfa",
  },
  {
    name: "Pétrole & Or",
    primary: "#12343b",
    secondary: "#d4a017",
  },
  {
    name: "Aubergine & Abricot",
    primary: "#3b1d3f",
    secondary: "#fb923c",
  },
  {
    name: "Basalte & Azur",
    primary: "#18181b",
    secondary: "#38bdf8",
  },
  {
    name: "Cobalt & Émeraude",
    primary: "#1d4ed8",
    secondary: "#10b981",
  },
  {
    name: "Bordeaux & Champagne",
    primary: "#4c0519",
    secondary: "#fbbf24",
  },
  {
    name: "Forêt & Turquoise",
    primary: "#064e3b",
    secondary: "#14b8a6",
  },
  {
    name: "Minuit & Lilas",
    primary: "#111827",
    secondary: "#c084fc",
  },
  {
    name: "Océan & Mandarine",
    primary: "#0c4a6e",
    secondary: "#f97316",
  },
  {
    name: "Charbon & Framboise",
    primary: "#1f2937",
    secondary: "#ec4899",
  },
  {
    name: "Nocturne & Jade",
    primary: "#0b1120",
    secondary: "#22c55e",
  },
];

const errors = computed(() => ({
  primary: isThemeColorHex(form.primary) ? "" : "Saisissez une couleur hexadécimale valide, par exemple #172033.",
  secondary: isThemeColorHex(form.secondary) ? "" : "Saisissez une couleur hexadécimale valide, par exemple #10bb82.",
  menuHover: form.menuHover && !isThemeColorHex(form.menuHover)
    ? "Saisissez une couleur hexadécimale valide, par exemple #1d3b35."
    : "",
}));

const hasCustomMenuHover = computed(() => Boolean(form.menuHover));
const hasErrors = computed(() => Boolean(errors.value.primary || errors.value.secondary || errors.value.menuHover));
const hasChanges = computed(
  () => form.primary.toLowerCase() !== savedColors.value.primary.toLowerCase()
    || form.secondary.toLowerCase() !== savedColors.value.secondary.toLowerCase()
    || (form.menuHover ?? "").toLowerCase() !== (savedColors.value.menuHover ?? "").toLowerCase(),
);
const previewColors = computed(() => (hasErrors.value ? savedColors.value : form));
const menuHoverBackground = computed(
  () => previewColors.value.menuHover ?? `color-mix(in srgb, ${previewColors.value.secondary}, transparent 88%)`,
);
const previewSecondaryContrastColor = computed(() => getPreviewContrastColor(previewColors.value.secondary));

// Each preview rebuilds every PrimeVue style: wait for the picker to settle.
let isUnmounted = false;
const previewForm = useDebounceFn(() => {
  if (!isUnmounted && !hasErrors.value) {
    previewThemeColors(form);
  }
}, 100);

watch(() => [form.primary, form.secondary, form.menuHover], () => previewForm());

// Restore what the store holds, not savedColors: a save still waiting for the API has
// already updated the store (and rolls it back itself if the API fails).
onBeforeUnmount(() => {
  isUnmounted = true;
  previewThemeColors(getThemeColors());
});

async function saveColors() {
  if (hasErrors.value) {
    return;
  }

  isSaving.value = true;
  statusMessage.value = null;

  try {
    await saveThemeColors(form);
    savedColors.value = { ...getThemeColors() };
    if (dialogRef?.value) {
      dialogRef.value.close();
      return;
    }
    statusMessage.value = {
      severity: "success",
      message: "Couleurs enregistrées pour votre utilisateur.",
    };
  }
  catch (error) {
    console.error("Erreur lors de l'enregistrement des couleurs:", error);
    statusMessage.value = {
      severity: "error",
      message: "Impossible d'enregistrer vos couleurs.",
    };
  }
  finally {
    isSaving.value = false;
  }
}

function cancelPreview() {
  form.primary = savedColors.value.primary;
  form.secondary = savedColors.value.secondary;
  form.menuHover = savedColors.value.menuHover;
  previewThemeColors(form);
  statusMessage.value = null;
}

function resetToDefaults() {
  form.primary = defaultThemeColors.primary;
  form.secondary = defaultThemeColors.secondary;
  form.menuHover = undefined;
  previewThemeColors(form);
  statusMessage.value = {
    severity: "info",
    message: "Valeurs par défaut appliquées en aperçu. Enregistrez pour les conserver.",
  };
}

function applySuggestion(suggestion: ColorSuggestion) {
  form.primary = suggestion.primary;
  form.secondary = suggestion.secondary;
  form.menuHover = undefined;
  statusMessage.value = null;
}

function isActiveSuggestion(suggestion: ColorSuggestion) {
  return form.primary.toLowerCase() === suggestion.primary.toLowerCase()
    && form.secondary.toLowerCase() === suggestion.secondary.toLowerCase()
    && !form.menuHover;
}

function toggleCustomMenuHover(event: Event) {
  const checked = (event.target as HTMLInputElement).checked;

  form.menuHover = checked ? getDefaultMenuHoverHex(form.primary, form.secondary) : undefined;
}

function getPreviewContrastColor(hex: string) {
  const value = hex.replace("#", "");
  const r = Number.parseInt(value.slice(0, 2), 16);
  const g = Number.parseInt(value.slice(2, 4), 16);
  const b = Number.parseInt(value.slice(4, 6), 16);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

  return luminance > 0.58 ? "#111827" : "#ffffff";
}

function getDefaultMenuHoverHex(primary: string, secondary: string) {
  const safePrimary = isThemeColorHex(primary) ? primary : defaultThemeColors.primary;
  const safeSecondary = isThemeColorHex(secondary) ? secondary : defaultThemeColors.secondary;

  return mixColors(safeSecondary, safePrimary, 0.88);
}

function mixColors(hex: string, targetHex: string, targetWeight: number) {
  const source = hexToRgb(hex);
  const target = hexToRgb(targetHex);
  const sourceWeight = 1 - targetWeight;

  return `#${toHex(source.r * sourceWeight + target.r * targetWeight)}${toHex(
    source.g * sourceWeight + target.g * targetWeight,
  )}${toHex(source.b * sourceWeight + target.b * targetWeight)}`;
}

function hexToRgb(hex: string) {
  const rawValue = hex.replace("#", "");
  const value = rawValue.length === 3
    ? rawValue.split("").map(char => `${char}${char}`).join("")
    : rawValue;

  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  };
}

function toHex(value: number) {
  return Math.round(Math.max(0, Math.min(255, value)))
    .toString(16)
    .padStart(2, "0");
}
</script>
